import {
  deleteAvsInternalAccount,
  editAvsInternalAccount,
  addAvsInternalAccount,
} from "@/app/lib/actions/avs-internal-accounts";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useAvsInternalAccountsQuery,
  invalidateAvsInternalAccountsQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addAvsInternalAccountSchema,
  editAvsInternalAccountSchema,
} from "@/lib/schemas/avs-internal-accounts";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

type AvsInternalAccount = {
  id: number;
  sellingType: string;
  label: string;
  brandLetter: string;
  accountNumber: string;
  kvps: string;
  isActive: boolean;
};

function AvsInternalAccountItemsListCard() {
  const queryClient = useQueryClient();

  const avsInternalAccountsQuery = useAvsInternalAccountsQuery({
    sort: { label: "asc" },
  });
  const handleCreate = async (
    avsInternalAccount: z.infer<typeof addAvsInternalAccountSchema>,
  ) => {
    const response = await addAvsInternalAccount(avsInternalAccount);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau compte interne AVS crée.");
      invalidateAvsInternalAccountsQuery(queryClient);
    }
  };

  const handleUpdate = async (
    avsInternalAccount: z.infer<typeof editAvsInternalAccountSchema>,
  ) => {
    const response = await editAvsInternalAccount(avsInternalAccount);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Compte interne AVS modifié.");
      invalidateAvsInternalAccountsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteAvsInternalAccount(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Compte interne AVS supprimé.");
      invalidateAvsInternalAccountsQuery(queryClient);
    }
  };

  if (avsInternalAccountsQuery.isPending) return <ItemsListCardSkeleton />;
  const avsInternalAccounts =
    avsInternalAccountsQuery.data?.avsInternalAccounts ?? [];

  return (
    <ItemsListCard<AvsInternalAccount>
      title="Comptes internes AVS"
      items={avsInternalAccounts}
      createSchema={addAvsInternalAccountSchema}
      editSchema={editAvsInternalAccountSchema}
      defaultCreateValues={{
        sellingType: "",
        label: "",
        brandLetter: "",
        accountNumber: "",
        kvps: "",
        isActive: true,
      }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.label}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="label">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={50}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="sellingType">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Type de vente</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={20}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="brandLetter">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Marque</FieldLabel>
                  <Select
                    name={field.name}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    aria-invalid={isInvalid}
                  >
                    <SelectTrigger
                      className="w-[180px]"
                      aria-invalid={isInvalid}
                    >
                      <SelectValue placeholder="Sélectionner une marque" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="A">Audi</SelectItem>
                        <SelectItem value="V">Volkswagen</SelectItem>
                        <SelectItem value="N">Volkswagen utilitaire</SelectItem>
                        <SelectItem value="S">Seat</SelectItem>
                        <SelectItem value="C">Skoda</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="accountNumber">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>N° de compte</FieldLabel>
                  <Input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={9}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="kvps">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Identifiant KVPS</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={8}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>{" "}
                <Switch
                  name={field.name}
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { AvsInternalAccountItemsListCard };
